// ===== Packages =====

import {
    useEffect,
    useRef,
    useState,
}                                               from 'react';

// ===== Hooks =====

import {
    usePerformanceStats,
}                                               from '.';

// ===== Interfaces =====

import {
    PerfStats,
}                                               from './usePerformanceStats';

// ===== Constants =====

import {
    USE_HEAVY_COMPUTE_GATE_FPS_WARN,
    USE_HEAVY_COMPUTE_GATE_TBT_WARN_PER_SEC,
    USE_HEAVY_COMPUTE_GATE_TBT_CRIT_PER_SEC,
    USE_HEAVY_COMPUTE_GATE_HEAP_WARN_RATIO,
    USE_HEAVY_COMPUTE_GATE_WORKER_SLOW_MS,
    USE_HEAVY_COMPUTE_GATE_BAD_TO_TRIP,
    USE_HEAVY_COMPUTE_GATE_GOOD_TO_RESET,
    USE_HEAVY_COMPUTE_GATE_SAMPLE_MS,
}                                               from '../constants/generalConstants';

type GateOptions = {
  fpsWarn: number;
  tbtWarnPerSec: number; // ms per second
  tbtCritPerSec: number; // ms per second
  heapWarnRatio: number; // used / total
  workerSlowMs: number;  // if initial worker bench slower than this, start OFF
  badToTrip: number;     // consecutive bad samples to turn OFF
  goodToReset: number;   // consecutive good samples to turn back ON
  sampleMs: number;      // sampling cadence
};

type GateState = {
  off: boolean;
  reason?: string;
};

const DEFAULTS: GateOptions = {
    fpsWarn: USE_HEAVY_COMPUTE_GATE_FPS_WARN,
    tbtWarnPerSec: USE_HEAVY_COMPUTE_GATE_TBT_WARN_PER_SEC,
    tbtCritPerSec: USE_HEAVY_COMPUTE_GATE_TBT_CRIT_PER_SEC,
    heapWarnRatio: USE_HEAVY_COMPUTE_GATE_HEAP_WARN_RATIO,
    workerSlowMs: USE_HEAVY_COMPUTE_GATE_WORKER_SLOW_MS,
    badToTrip: USE_HEAVY_COMPUTE_GATE_BAD_TO_TRIP,
    goodToReset: USE_HEAVY_COMPUTE_GATE_GOOD_TO_RESET,
    sampleMs: USE_HEAVY_COMPUTE_GATE_SAMPLE_MS,
};

export default function useHeavyComputeGate(overrides?: Partial<GateOptions>): {
    stats: PerfStats,
    off: boolean,
    reason?: string,
} {
    const cfg = { ...DEFAULTS, ...overrides };
    const stats = usePerformanceStats(cfg.sampleMs);

    const [state, setState] = useState<GateState>(() => ({
        off: stats.workerBenchMs != null && stats.workerBenchMs > cfg.workerSlowMs,
        reason: stats.workerBenchMs != null && stats.workerBenchMs > cfg.workerSlowMs
            ? `slow CPU baseline (${stats.workerBenchMs}ms)`
            : undefined,
    }));

    // streak counters + TBT tracking
    const badStreak = useRef(0);
    const goodStreak = useRef(0);
    const prevTbt = useRef<number | null>(null);
    const prevTbtTs = useRef<number | null>(null);
    const latestStats = useRef(stats);

    useEffect(() => {
        latestStats.current = stats;
    }, [stats]);

    useEffect(() => {
        const id = setInterval(
            () => {
                const s = latestStats.current;
                const now = performance.now();

                // Compute TBT per second from cumulative total
                let tbtPerSec = 0;
                const total = s.longTasks?.totalBlockingTime ?? null;
                if (total != null) {
                    if (prevTbt.current != null && prevTbtTs.current != null) {
                        const dt = Math.max(1, now - prevTbtTs.current); // ms
                        const delta = Math.max(0, total - prevTbt.current); // ms
                        tbtPerSec = (delta * 1000) / dt; // ms/s
                    }
                    prevTbt.current = total;
                    prevTbtTs.current = now;
                }

                // Heap pressure (Chrome-only)
                let heapRatio = 0;
                if (s.jsHeap?.totalJSHeapSize && s.jsHeap.usedJSHeapSize) {
                    heapRatio = s.jsHeap.usedJSHeapSize / s.jsHeap.totalJSHeapSize;
                }

                // Decide if this sample is "bad"
                const fpsBad = s.fps != null && s.fps < cfg.fpsWarn;
                const tbtBad = tbtPerSec >= cfg.tbtWarnPerSec;
                const tbtCrit = tbtPerSec >= cfg.tbtCritPerSec;
                const heapBad = heapRatio >= cfg.heapWarnRatio;

                const isBad = fpsBad || tbtBad || heapBad;

                if (tbtCrit) {
                // immediate penalty on critical jank
                    badStreak.current = cfg.badToTrip;
                } else if (isBad) {
                    badStreak.current += 1;
                    goodStreak.current = 0;
                } else {
                    goodStreak.current += 1;
                    badStreak.current = Math.max(0, badStreak.current - 1); // decay
                }

                // Transition rules
                if (!state.off && badStreak.current >= cfg.badToTrip) {
                    // eslint-disable-next-line no-nested-ternary
                    const reason = tbtCrit
                        ? `TBT spike (${Math.round(tbtPerSec)}ms/s)`
                        // eslint-disable-next-line no-nested-ternary
                        : fpsBad
                            ? `low FPS (${s.fps})`
                            : heapBad
                                ? `high heap (${Math.round(heapRatio * 100)}%)`
                                : `blocking (${Math.round(tbtPerSec)}ms/s)`;
                    setState({ off: true, reason });
                    goodStreak.current = 0;
                } else if (state.off && goodStreak.current >= cfg.goodToReset) {
                    setState({ off: false, reason: undefined });
                    badStreak.current = 0;
                }
            },
            cfg.sampleMs,
        );

        return () => clearInterval(id);
    }, [
        cfg.sampleMs,
        cfg.badToTrip,
        cfg.goodToReset,
        cfg.fpsWarn,
        cfg.tbtWarnPerSec,
        cfg.tbtCritPerSec,
        cfg.heapWarnRatio,
        state.off,
    ]);

    return {
        ...state,
        stats,
    };
}
